import { UPDATES_ENDTIME } from '@/constants';
import { getUserInfo, removeUserInfo } from '@/functions/tokenSettings';
import User, { UserTypeEnum } from '@/models/User';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import moment from 'moment';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useDeleteUpdates from '../hook/useDeleteUpdates';
import { lessonsProgressesQueryKeys, meQueryKeys } from '../services/querykeys';
import { getRequestObserver } from '@/lib/axios';
import { RequestEvent } from '@/utils/observers/RequestObserver';
import { logout as logoutRequest } from '../services/authService';

export interface AuthContextProps {
  user?: User;
  isLoading: boolean;
  signup?: (username: string, password: string) => Promise<void>;
  logout?: () => void;
  handleAccessConfig?(): void;
}

export const AuthContext = createContext<AuthContextProps>({
  isLoading: false,
});

export function AuthProvider(props: any) {
  const queryClient = useQueryClient();
  const [userInfo, setUserInfo] = useState(() => getUserInfo());
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isLoggingOutRef = useRef(false);

  const { mutate: logout } = useMutation(logoutRequest, {
    onSettled: () => {
      removeUserInfo();
      setUserInfo(undefined);
      queryClient.removeQueries();
      isLoggingOutRef.current = false;
      navigate('/login');
    },
  });

  const { data: rawUser, isInitialLoading: loadingMe } = useQuery({
    ...meQueryKeys.get(userInfo?.userId ?? ''),
    retry: (failureCount, error) => {
      if ((error as AxiosError)?.response?.status === 401) return false;
      return failureCount < 3;
    },
    enabled: !pathname.includes('login') && !!userInfo,
  });

  const user = useMemo<User | undefined>(() => {
    if (!rawUser || !userInfo) return rawUser;
    return { ...rawUser, userType: userInfo.userType as UserTypeEnum };
  }, [rawUser, userInfo]);

  useEffect(() => {
    if (user) moment.tz.setDefault(user.timezone);
  }, [user]);

  const handleAccessConfig = useCallback(() => {
    setUserInfo(getUserInfo());
    queryClient.invalidateQueries({ queryKey: meQueryKeys.get._def });
    navigate('/');
  }, [queryClient, navigate]);

  useEffect(() => {
    const observerId = 'auth-refresh-failure';
    const requestObserver = getRequestObserver();
    const unsubscribe = () => requestObserver.unsubscribe(observerId);
    const isLoginPage = pathname.includes('login');

    if (isLoginPage) return () => unsubscribe();

    const onRefreshFailure = (event: RequestEvent) => {
      const isRefreshRoute = /\/auth\/token\/refresh\/?$/.test(event.url);
      const isUnauthorized = event.statusCode === 401;

      if (!isRefreshRoute || !isUnauthorized) return;
      if (isLoginPage) return;
      if (isLoggingOutRef.current) return;
      isLoggingOutRef.current = true;
      logout();
    };

    requestObserver.subscribe({ id: observerId, action: onRefreshFailure });

    return () => unsubscribe();
  }, [navigate, logout, pathname]);

  useEffect(() => {
    queryClient.removeQueries(lessonsProgressesQueryKeys.list());
  }, [queryClient]);

  useDeleteUpdates({
    dueDate: moment()
      .subtract(...UPDATES_ENDTIME)
      .toDate(),
    keywords: ['deleteExtraLesson', 'notificationsPage', 'teachingWeek'],
  });

  const value = useMemo<AuthContextProps>(
    () => ({ user, isLoading: loadingMe, logout, handleAccessConfig }),
    [user, loadingMe, logout, handleAccessConfig],
  );

  return (
    <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>
  );
}

export default AuthContext;
